import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Receipt, Spinner, Trash, Plus, Minus, X as XIcon, Buildings, CheckCircle, Share, Clock, PencilSimple, EnvelopeSimple, ShoppingCart, CaretDown, CaretUp, FilePdf, GitBranch, ListChecks, BookOpen } from "@phosphor-icons/react";
import {
  fetchQuote, updateQuoteLine, deleteQuoteLine, updateQuoteHeading,
  createQuoteShareLink, fetchQuoteHistory, promoteQuote, sendQuoteToCustomer,
  fetchQuoteRelated, fetchQuoteVersions, quotePdfUrl, fetchQuoteEmailSends,
  fetchQuotePmCandidates, pmSchedulePdfUrl,
  fetchQuoteDealerState, submitQuoteForApproval,
} from "@/lib/api";
import DealerQuoteWizard from "@/components/help/DealerQuoteWizard";
import ApprovalHistoryTimeline from "@/components/dealer-quote/ApprovalHistoryTimeline";
import { formatMoney } from "@/lib/cart";
import { useT } from "@/lib/i18n";
import ShareLinkBox from "@/components/ShareLinkBox";
import QuoteCommentsThread from "@/components/QuoteCommentsThread";
import QuotePaymentChip from "@/components/QuotePaymentChip";
import PayNowButton from "@/components/PayNowButton";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";

export default function QuoteDetailPage({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("t") || "";
  const isAdmin = Boolean(user?.is_admin);
  const t = useT();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [banner, setBanner] = useState("");
  const [shareLink, setShareLink] = useState("");
  const [generatingShare, setGeneratingShare] = useState(false);

  // History (visible to everyone who can see the quote)
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Related quotes (same customer) & version diff
  const [related, setRelated] = useState([]);
  const [versions, setVersions] = useState(null);
  const [versionsExpanded, setVersionsExpanded] = useState(false);

  // Email tracking — map of send_id → {status, opens[], bounced, ...}
  const [emailSends, setEmailSends] = useState([]);
  // iter150AI — Dealer workflow state (lock + submit gate).
  const [dealerState, setDealerState] = useState(null);
  const loadDealerState = useCallback(async () => {
    try { setDealerState(await fetchQuoteDealerState(id)); }
    catch { setDealerState(null); }
  }, [id]);
  // iter150AI.2 — wizard modal toggle.
  const [wizardOpen, setWizardOpen] = useState(false);
  const loadEmailSends = useCallback(async () => {
    try {
      const params = magicToken ? { t: magicToken } : {};
      const d = await fetchQuoteEmailSends(id, params);
      setEmailSends(d?.sends || []);
    } catch { setEmailSends([]); }
  }, [id, magicToken]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const d = await fetchQuoteHistory(id);
      setHistory(d?.events || []);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  }, [id]);

  const loadRelated = useCallback(async () => {
    try {
      const params = magicToken ? { t: magicToken } : {};
      const d = await fetchQuoteRelated(id, params);
      setRelated(d?.quotes || []);
    } catch { setRelated([]); }
  }, [id, magicToken]);

  const loadVersions = useCallback(async () => {
    try {
      const params = magicToken ? { t: magicToken } : {};
      const d = await fetchQuoteVersions(id, params);
      setVersions(d);
    } catch { setVersions(null); }
  }, [id, magicToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await fetchQuote(id);
      setData(d);
    } catch (e) {
      // Distinguish 404 (quote missing or token expired) from other errors so
      // the i18n fallback `quote.not_found` always renders translated rather
      // than echoing the backend's English `detail` string verbatim.
      const status = e?.response?.status;
      if (status === 404) {
        setError("__not_found__");
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to load quote");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); loadHistory(); loadRelated(); loadVersions(); loadEmailSends(); loadDealerState(); }, [load, loadHistory, loadRelated, loadVersions, loadEmailSends, loadDealerState]);
  // Poll email-sends every 30s so opens/bounces appear live without a manual refresh
  useEffect(() => {
    const t = setInterval(() => { loadEmailSends(); }, 30000);
    return () => clearInterval(t);
  }, [loadEmailSends]);

  const showBanner = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(""), 2500);
  };

  // Detect currency from first line (fallback USD)
  const currency = data?.heading?.Company?.startsWith("CART-") ? "USD" : "USD"; // simplistic

  const isInvoice = Boolean(data?.heading?.invoice_number);
  const canEdit = isAdmin && !isInvoice;

  // Pay-now state (Branch B). Fetch the live balance for this quote so the
  // button can render the exact amount to collect.
  const [paymentInfo, setPaymentInfo] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    import("@/lib/api").then((m) =>
      m.fetchQuotePayments(id, magicToken ? { t: magicToken } : {})
        .then((p) => { if (!cancelled) setPaymentInfo(p); })
        .catch(() => {})
    );
    return () => { cancelled = true; };
  }, [id, magicToken]);

  const onUpdateQty = useCallback(async (detailId, newQty) => {
    const q = Math.max(1, Number(newQty) || 1);
    setSavingId(detailId);
    try {
      const res = await updateQuoteLine(id, detailId, { quantity: q });
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          details: prev.details.map((d) =>
            d.QuoteInvoiceDetailID === detailId ? { ...d, Quantity: q } : d
          ),
          heading: { ...prev.heading, total: res?.new_total ?? prev.heading.total },
        };
      });
      showBanner("Updated");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Update failed");
    } finally {
      setSavingId(null);
      loadHistory();
      loadVersions();
    }
  }, [id, loadHistory, loadVersions]);

  const onUpdatePrice = useCallback(async (detailId, newPrice) => {
    const p = Math.max(0, Number(newPrice) || 0);
    setSavingId(detailId);
    try {
      const res = await updateQuoteLine(id, detailId, { price: p });
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          details: prev.details.map((d) =>
            d.QuoteInvoiceDetailID === detailId ? { ...d, price: p } : d
          ),
          heading: { ...prev.heading, total: res?.new_total ?? prev.heading.total },
        };
      });
      showBanner("Price updated");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Update failed");
    } finally {
      setSavingId(null);
      loadHistory();
      loadVersions();
    }
  }, [id, loadHistory, loadVersions]);

  const onDelete = useCallback(async (detailId, label) => {
    if (!window.confirm(`Remove "${label}" from this quote?`)) return;
    setSavingId(detailId);
    try {
      const res = await deleteQuoteLine(id, detailId);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          details: prev.details.filter((d) => d.QuoteInvoiceDetailID !== detailId),
          heading: { ...prev.heading, total: res?.new_total ?? prev.heading.total },
          detail_count: Math.max(0, (prev.detail_count || 0) - 1),
        };
      });
      showBanner("Line removed");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally {
      setSavingId(null);
      loadHistory();
      loadVersions();
    }
  }, [id, loadHistory, loadVersions]);

  const onUpdateHeading = useCallback(async (patch, successMsg) => {
    try {
      await updateQuoteHeading(id, patch);
      setData((prev) => prev ? { ...prev, heading: { ...prev.heading, ...patch } } : prev);
      showBanner(successMsg || "Saved");
      // Reload to get recomputed total
      load();
      loadHistory();
      loadVersions();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    }
  }, [id, load, loadHistory, loadVersions]);

  const onGenerateShareLink = useCallback(async () => {
    setGeneratingShare(true);
    try {
      const res = await createQuoteShareLink(id);
      // Always rebuild the absolute URL from the browser's own origin — the
      // backend's guess may point at an internal ingress host.
      const absolute = `${window.location.origin}/quote/${encodeURIComponent(id)}?t=${encodeURIComponent(res.token)}`;
      setShareLink(absolute);
      showBanner("Share link generated");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to generate share link");
    } finally {
      setGeneratingShare(false);
    }
  }, [id]);

  const [promoting, setPromoting] = useState(false);
  const onPromoteToQuote = useCallback(async () => {
    if (!window.confirm("Promote this Preliminary draft to a final Quote? The buyer will be notified by email.")) return;
    setPromoting(true);
    try {
      await promoteQuote(id);
      showBanner("Promoted to final Quote");
      await load();
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Promotion failed");
    } finally {
      setPromoting(false);
    }
  }, [id, load, loadHistory]);

  // Send quote to customer (admin OR prepared_by)
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sendResult, setSendResult] = useState(null);

  // PM Schedule attachment — wired into the quote send flow
  const [pmCandidates, setPmCandidates] = useState([]);
  const [pmLoaded, setPmLoaded] = useState(false);
  const [pmCurrency, setPmCurrency] = useState("USD");
  const [pmBrand, setPmBrand] = useState("US");
  const [includePm, setIncludePm] = useState(false);
  const [pmProductType, setPmProductType] = useState("");
  const [pmShifts, setPmShifts] = useState(1);
  const [pmYears, setPmYears] = useState(10);
  const [pmIncludePrices, setPmIncludePrices] = useState(true);

  // Lazy-load PM candidates the first time the user opens the Send panel
  useEffect(() => {
    if (!sendOpen || pmLoaded) return;
    (async () => {
      try {
        const d = await fetchQuotePmCandidates(id);
        const cands = d?.candidates || [];
        setPmCandidates(cands);
        setPmCurrency(d?.currency || "USD");
        setPmBrand(d?.brand || "US");
        if (cands.length > 0 && !pmProductType) setPmProductType(cands[0].product_code);
      } catch {
        setPmCandidates([]);
      } finally {
        setPmLoaded(true);
      }
    })();
  }, [sendOpen, pmLoaded, id, pmProductType]);

  const onSendQuote = useCallback(async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await sendQuoteToCustomer(id, {
        to_email: (sendTo || "").trim(),
        note: sendNote || "",
        include_pm: !!(includePm && pmProductType),
        pm_product_type: pmProductType || "",
        pm_shifts: Number(pmShifts || 1),
        pm_years: Number(pmYears || 10),
        pm_include_prices: !!pmIncludePrices,
      });
      setSendResult(res);
      showBanner(`Sent to ${res.sent_to?.length || 0} recipient${(res.sent_to?.length || 0) === 1 ? "" : "s"}`);
      loadHistory();
    } catch (e) {
      setSendResult({ ok: false, error: e?.response?.data?.detail || e?.message || "Send failed" });
    } finally {
      setSending(false);
    }
  }, [id, sendTo, sendNote, includePm, pmProductType, pmShifts, pmYears, pmIncludePrices, loadHistory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    const isNotFound = error === "__not_found__" || !error;
    const msg = isNotFound ? t("quote.not_found", "Quote not found") : error;
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-sm p-6 text-center">
          <p className="text-sm font-bold text-red-700 mb-2">{msg}</p>
          <button onClick={() => navigate(-1)} className="text-xs text-slate-500 underline">{t("quote.go_back", "Go back")}</button>
        </div>
      </div>
    );
  }

  const h = data.heading;
  const lines = data.details || [];
  const subtotal = lines.reduce((s, d) => s + (Number(d.Quantity) || 0) * (Number(d.price) || 0) * (1 - (Number(d.discount) || 0)), 0);
  const shortId = (h.QuoteInvoiceHeadingID || "").startsWith("CART-")
    ? h.QuoteInvoiceHeadingID.slice(5, 13)
    : (h.QuoteInvoiceHeadingID || "").slice(0, 8);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 h-14 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={t("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <Receipt size={20} weight="bold" className="text-blue-700" />
            <h1 className="text-lg font-black tracking-tighter font-['Chivo']">
              {isInvoice ? t("quote.invoice", "Quote") : t("quote.title", "Quote")} <span className="font-mono text-blue-700">#{shortId}</span>
            </h1>
            {h.Status && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm ml-2">{h.Status === "Preliminary" ? t("quote.preliminary", "Preliminary") : h.Status}</span>
            )}
            {isInvoice && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-sm ml-1">{t("quote.invoice", "Invoice")} — Locked</span>
            )}
            {/* Payment-status chip — pulled live from /api/quotes/{id}/payments */}
            <QuotePaymentChip
              headingId={h.QuoteInvoiceHeadingID}
              magicToken={magicToken}
              customerId={h.Company}
            />
            <AIProvenanceBadge entityType="quote" entityId={h.QuoteInvoiceHeadingID} userEmail={user?.email} />
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
            <a
              data-testid="quote-download-pdf"
              href={quotePdfUrl(id, magicToken)}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold text-[11px] uppercase tracking-wider"
              title={t("quote.download_pi_tip", "Download Proforma Invoice (PDF)")}
            >
              <FilePdf size={12} weight="bold" /> <span className="hidden sm:inline">Download&nbsp;PI</span>
            </a>
            <span className="hidden md:inline max-w-[200px] truncate">{user?.email || "Guest"}</span>
            {onLogout && user && (
              <button onClick={onLogout} className="text-xs text-slate-400 hover:text-slate-700 underline">{t("compressor_page.sign_out", "Sign out")}</button>
            )}
          </div>
        </div>
      </header>

      {banner && (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 mt-3">
          <div data-testid="quote-banner" className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold px-2 py-1 rounded-sm">
            <CheckCircle size={12} weight="fill" /> {banner}
          </div>
        </div>
      )}
      {error && (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 mt-3">
          <div className="flex items-center justify-between gap-2 bg-red-50 text-red-700 border border-red-200 text-xs font-semibold px-3 py-2 rounded-sm">
            <span>{error}</span>
            <button onClick={() => setError("")} className="text-red-500 hover:text-red-800"><XIcon size={12} /></button>
          </div>
        </div>
      )}

      {/* iter150AI — Dealer workflow banner + submit button. */}
      <DealerWorkflowBar
        dealerState={dealerState}
        headingId={id}
        onChanged={async () => { await load(); await loadDealerState(); }}
        onError={(msg) => setError(msg)}
        onBanner={(msg) => setBanner(msg)}
        onShowWizard={() => setWizardOpen(true)}
      />

      <main className="max-w-[1200px] mx-auto p-4 md:p-8 grid md:grid-cols-[1fr_340px] gap-6">
        <section className="space-y-5">
          {/* Magic-link indicator (when viewing via ?t=) */}
          {magicToken && !user?.is_admin && (
            <div data-testid="quote-magic-link-notice" className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-blue-800">
              <Share size={12} weight="bold" />
              <span>{t("quote.shared_link_readonly", "You're viewing this quote via a shared link (read-only). No account needed.")}</span>
            </div>
          )}

          {/* Admin: generate / copy share link */}
          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              {shareLink ? (
                <ShareLinkBox
                  link={shareLink}
                  label={t("quote.share_this_quote", "Share this quote")}
                  description={t("quote.share_description", "Anyone with this link can view the quote read-only for the next 90 days. Regenerate to invalidate.")}
                  testidPrefix="quote-share-link"
                />
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Share size={14} weight="bold" className="text-blue-700 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900">{t("quote.shareable_link", "Shareable link")}</p>
                      <p className="text-[11px] text-slate-500">{t("quote.shareable_link_hint", "Generate a public URL anyone can open — great for forwarding to decision-makers.")}</p>
                    </div>
                  </div>
                  <button
                    data-testid="quote-generate-share-link"
                    onClick={onGenerateShareLink}
                    disabled={generatingShare}
                    className="h-9 px-3 text-xs font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 whitespace-nowrap flex items-center gap-1"
                  >
                    {generatingShare ? <><Spinner size={11} className="animate-spin" /> Generating…</> : <><Share size={11} weight="bold" /> Generate</>}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Admin: promote preliminary → Quote */}
          {isAdmin && String(h.Status || "").toLowerCase() === "preliminary" && (
            <div data-testid="quote-promote-card" className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-sm p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-slate-900">{t("quote.preliminary_draft", "This is a Preliminary draft")}</p>
                <p className="text-[11px] text-amber-800">{t("quote.promote_hint", "Promote it to a final Quote when you're happy with the line items. The buyer will be notified by email.")}</p>
              </div>
              <button
                data-testid="quote-promote-button"
                disabled={promoting}
                onClick={onPromoteToQuote}
                className="inline-flex items-center gap-1.5 h-10 px-4 text-sm font-bold rounded-sm text-white bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 whitespace-nowrap"
              >
                {promoting ? <><Spinner size={12} className="animate-spin" /> Promoting…</> : <>{t("quote.promote_to_quote", "Promote to Quote →")}</>}
              </button>
            </div>
          )}

          {/* Send quote to customer — admin OR the user who prepared the quote */}
          {(() => {
            const preparerEmail = String(h.prepared_by || "").trim().toLowerCase();
            const myEmail = String(user?.email || "").trim().toLowerCase();
            const canSend = !isInvoice && (isAdmin || (preparerEmail && preparerEmail === myEmail));
            if (!canSend) return null;
            return (
              <div data-testid="quote-send-card" className="bg-white border border-slate-200 rounded-sm p-4">
                {!sendOpen ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <EnvelopeSimple size={14} weight="bold" className="text-blue-700 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900">{t("quote.send_to_customer", "Send quote to customer")}</p>
                        <p className="text-[11px] text-slate-500">{t("quote.send_to_customer_hint", "Emails the PDF + a magic link with the right brand sender and Reply-To.")}</p>
                      </div>
                    </div>
                    <button
                      data-testid="quote-send-open"
                      onClick={() => setSendOpen(true)}
                      className="h-9 px-3 text-xs font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 whitespace-nowrap inline-flex items-center gap-1.5"
                    >
                      <EnvelopeSimple size={11} weight="bold" /> Send email
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <EnvelopeSimple size={14} weight="bold" className="text-blue-700" />
                      <p className="text-sm font-bold text-slate-900">{t("quote.send_to_customer", "Send quote to customer")}</p>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("quote.recipient_label", "Recipient (leave blank to use customer on file)")}</label>
                      <input
                        data-testid="quote-send-to"
                        type="email"
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        placeholder="customer@example.com"
                        className="w-full h-9 px-2.5 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{t("quote.note_label", "Note (optional — shown in email body)")}</label>
                      <textarea
                        data-testid="quote-send-note"
                        value={sendNote}
                        onChange={(e) => setSendNote(e.target.value)}
                        placeholder={t("quote.note_placeholder", "Per our call, attached is the revised quote…")}
                        rows={3}
                        className="w-full px-2.5 py-2 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* ── PM Schedule attach panel ─────────────────── */}
                    <div data-testid="quote-send-pm-panel" className="border border-slate-200 rounded-sm bg-slate-50 p-3 space-y-2.5">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          data-testid="quote-send-pm-toggle"
                          type="checkbox"
                          checked={includePm}
                          onChange={(e) => setIncludePm(e.target.checked)}
                          disabled={pmLoaded && pmCandidates.length === 0}
                          className="mt-0.5 h-4 w-4 accent-emerald-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-slate-900">
                            {t("quote.attach_pm_schedule", "Attach Preventive-Maintenance Schedule")}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {pmLoaded && pmCandidates.length === 0
                              ? "No compressor line on this quote has PM data — attach not available."
                              : "Adds a second PDF with yearly service parts, cost, and PM/compressor ratio."}
                          </div>
                        </div>
                      </label>
                      {includePm && pmCandidates.length > 0 && (
                        <div className="space-y-2 pl-6">
                          <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Compressor</label>
                            <select
                              data-testid="quote-send-pm-compressor"
                              value={pmProductType}
                              onChange={(e) => setPmProductType(e.target.value)}
                              className="w-full h-9 px-2 text-sm rounded-sm border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                            >
                              {pmCandidates.map((c) => (
                                <option key={c.product_code} value={c.product_code}>
                                  {c.product_name || c.product_code}
                                  {c.quantity > 1 ? ` × ${c.quantity}` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Shifts</label>
                              <select
                                data-testid="quote-send-pm-shifts"
                                value={pmShifts}
                                onChange={(e) => setPmShifts(Number(e.target.value))}
                                className="w-full h-9 px-2 text-sm rounded-sm border border-slate-300 bg-white"
                              >
                                <option value={1}>{t("quote.shift_1", "1 shift · 3,000 h/yr")}</option>
                                <option value={2}>{t("quote.shift_2", "2 shifts · 6,000 h/yr")}</option>
                                <option value={3}>{t("quote.shift_3", "3 shifts · 8,000 h/yr")}</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Years</label>
                              <select
                                data-testid="quote-send-pm-years"
                                value={pmYears}
                                onChange={(e) => setPmYears(Number(e.target.value))}
                                className="w-full h-9 px-2 text-sm rounded-sm border border-slate-300 bg-white"
                              >
                                {[1, 2, 3, 5, 7, 10].map((y) => (
                                  <option key={y} value={y}>{y} year{y === 1 ? "" : "s"}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
                            <input
                              data-testid="quote-send-pm-include-prices"
                              type="checkbox"
                              checked={pmIncludePrices}
                              onChange={(e) => setPmIncludePrices(e.target.checked)}
                              className="h-3.5 w-3.5 accent-emerald-600"
                            />
                            <span>Include prices &amp; PM-vs-compressor % in the PDF</span>
                          </label>
                          {pmProductType && (
                            <a
                              data-testid="quote-send-pm-preview"
                              href={pmSchedulePdfUrl({
                                product_type: pmProductType,
                                shifts: pmShifts,
                                years: pmYears,
                                brand: pmBrand,
                                customer_name: data?.heading?.Company || "",
                                quote_id: id,
                                include_prices: pmIncludePrices,
                              })}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-700 hover:underline"
                            >
                              <FilePdf size={12} weight="bold" /> Preview the PM PDF
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {sendResult && (
                      <div
                        data-testid="quote-send-result"
                        className={`text-xs leading-snug rounded-sm border px-3 py-2 ${
                          sendResult.ok
                            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                            : "bg-red-50 border-red-200 text-red-900"
                        }`}
                      >
                        {sendResult.ok ? (
                          <>
                            <div className="font-bold">{t("quote.sent_to", "Sent to")} {(sendResult.sent_to || []).length} recipient{(sendResult.sent_to || []).length === 1 ? "" : "s"}</div>
                            <div className="mt-1 break-all">
                              <b>Delivered:</b> {(sendResult.sent_to || []).join(", ")}
                            </div>
                            {(sendResult.attachments || []).length > 0 && (
                              <div className="mt-1 break-all">
                                <b>Attachments:</b> {(sendResult.attachments || []).join(", ")}
                              </div>
                            )}
                            {sendResult.pm_attached && (
                              <div data-testid="quote-send-pm-attached" className="mt-1 text-emerald-900">
                                {t("quote.pm_attached", "PM Schedule attached —")} {sendResult.pm_attached.product_type} · {sendResult.pm_attached.shifts} shift · {sendResult.pm_attached.years} yr
                                {!sendResult.pm_attached.include_prices ? " (no prices)" : ""}
                              </div>
                            )}
                            {!sendResult.smtp_ready && (
                              <div className="mt-1 text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
                                {t("quote.smtp_not_configured", "SMTP not configured — logged to server only (no real email sent).")}
                              </div>
                            )}
                          </>
                        ) : (
                          <><div className="font-bold">Failed</div><div className="mt-1">{sendResult.error || "Send failed"}</div></>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button
                        data-testid="quote-send-submit"
                        onClick={onSendQuote}
                        disabled={sending}
                        className="h-9 px-3 text-xs font-bold rounded-sm text-white bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 inline-flex items-center gap-1.5"
                      >
                        {sending ? <><Spinner size={11} className="animate-spin" /> Sending…</> : <><EnvelopeSimple size={11} weight="bold" /> Send now</>}
                      </button>
                      <button
                        data-testid="quote-send-cancel"
                        onClick={() => { setSendOpen(false); setSendResult(null); }}
                        className="h-9 px-3 text-xs font-semibold rounded-sm text-slate-600 hover:bg-slate-100"
                      >
                        {t("action.close", "Close")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Live buyer-engagement banner — shows when tracking pixel fired */}
          {(() => {
            const allOpens = (emailSends || []).flatMap(s => (s.opens || []).filter(o => !o.is_proxy).map(o => ({ ...o, to: s.to_email, trigger: s.trigger })));
            if (allOpens.length === 0) return null;
            allOpens.sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
            const hourAgoIso = new Date(Date.now() - 3600 * 1000).toISOString();
            const recent = allOpens.filter(o => String(o.ts) >= hourAgoIso).length;
            const last = allOpens[0];
            const bounced = (emailSends || []).filter(s => s.status === "bounced");
            return (
              <div data-testid="quote-engagement-banner" className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-sm p-3 flex items-start gap-3">
                <span aria-hidden="true" className="text-emerald-700 text-lg leading-none mt-0.5">👁</span>
                <div className="flex-1 min-w-0 text-sm text-slate-800">
                  <div className="font-bold text-emerald-900">
                    {recent > 0
                      ? <>{t("quote.buyer_activity", "Buyer activity:")} <span className="text-emerald-700">{recent} open{recent === 1 ? "" : "s"} {t("quote.in_last_hour", "in the last hour")}</span></>
                      : <>{t("quote.buyer_opened", "Buyer opened this quote")}</>}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    {allOpens.length} {t("quote.total_open", "total open")}{allOpens.length === 1 ? "" : "s"} {t("quote.last_viewed", "· last viewed")} {formatRel(last.ts)}
                    {last.to && <> {t("quote.email_sent_to", "· sent to")} <span className="font-mono">{last.to}</span></>}
                  </div>
                  {bounced.length > 0 && (
                    <div className="text-[11px] text-red-700 mt-0.5">
                      ⚠ {bounced.length} bounce{bounced.length === 1 ? "" : "s"} {t("quote.check_recipients", "— check recipient addresses")}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Buyer */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">
              <Buildings size={12} className="inline mr-1 mb-0.5" />Buyer / Bill-to
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <HField label={t("checkout.customer_id", "Customer ID")} value={h.Company} mono />
              <HField label={t("quote.quote_date", "Quote Date")} value={h.quote_date || "—"} />
              <HField label={t("checkout.prepared_by", "Prepared by")} value={h.prepared_by || "—"} mono />
              <HField label="Payment" value={h.Payment || "—"} />
              {h.po_number && <HField label={t("quote.po_number", "PO Number")} value={h.po_number} />}
              {h.Validity != null && <HField label={t("quote.validity_days", "Validity (days)")} value={h.Validity} />}
              {h.address && (
                <div className="md:col-span-2">
                  <HField label="Ship-to" value={h.address} />
                </div>
              )}
              {h.special_instructions && (
                <div className="md:col-span-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{t("quote.special_instructions", "Special instructions")}</p>
                  <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 italic">{h.special_instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Pay Now CTA — shows only for invoiced quotes with outstanding balance */}
          <PayNowButton
            headingId={id}
            magicToken={magicToken}
            isInvoice={isInvoice}
            balance={paymentInfo?.balance || 0}
          />

          {/* Lines */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-black uppercase tracking-wider text-slate-500">Items ({data.detail_count || lines.length})</h2>
              {canEdit && <p className="text-[10px] text-slate-400 italic">{t("quote.admin_edit_mode", "Admin edit mode — change qty/price or delete lines.")}</p>}
            </div>

            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 text-left">{t("table.product", "Product")}</th>
                    <th className="px-3 py-2 text-right w-[110px]">{t("table.qty", "Qty")}</th>
                    <th className="px-3 py-2 text-right w-[130px]">{t("table.price", "Price")}</th>
                    <th className="px-3 py-2 text-right w-[120px]">{t("checkout.line_total", "Line total")}</th>
                    {canEdit && <th className="px-3 py-2 text-center w-[40px]"></th>}
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={canEdit ? 5 : 4} className="px-3 py-10 text-center text-slate-400">{t("quote.no_line_items", "No line items")}</td></tr>
                  )}
                  {lines.map((d) => (
                    <QuoteLineRow
                      key={d.QuoteInvoiceDetailID}
                      line={d}
                      currency={currency}
                      canEdit={canEdit}
                      saving={savingId === d.QuoteInvoiceDetailID}
                      onUpdateQty={onUpdateQty}
                      onUpdatePrice={onUpdatePrice}
                      onDelete={onDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comments & replies thread */}
          <QuoteCommentsThread
            quoteId={id}
            magicToken={magicToken}
            user={user}
          />

          {/* Version diff — what changed since v1 */}
          <QuoteVersionDiffSection
            versions={versions}
            expanded={versionsExpanded}
            onToggle={() => setVersionsExpanded((v) => !v)}
            currency={currency}
          />

          {/* Change history */}
          <QuoteHistorySection
            events={history}
            loading={historyLoading}
            expanded={historyExpanded}
            onToggle={() => setHistoryExpanded((v) => !v)}
            onRefresh={() => { loadHistory(); loadEmailSends(); }}
            currency={currency}
            emailSends={emailSends}
          />
        </section>

        {/* Totals sidebar */}
        <aside className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-sm p-5 sticky top-20">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3">Totals</h2>
            <div className="space-y-1.5 text-sm">
              <SumRow label="Subtotal" value={formatMoney(subtotal, currency)} />
              {canEdit ? (
                <EditableNumRow label="Shipping" value={h.Shipping} onSave={(v) => onUpdateHeading({ Shipping: v }, "Shipping updated")} currency={currency} testid="quote-shipping" />
              ) : (Number(h.Shipping) > 0 && <SumRow label="Shipping" value={formatMoney(h.Shipping, currency)} />)}
              {canEdit ? (
                <EditableNumRow label={t("checkout.sales_tax_label", "Sales Tax")} value={h.sales_tax} onSave={(v) => onUpdateHeading({ sales_tax: v }, "Tax updated")} currency={currency} testid="quote-salestax" />
              ) : (Number(h.sales_tax) > 0 && <SumRow label={t("checkout.sales_tax_label", "Sales Tax")} value={formatMoney(h.sales_tax, currency)} />)}
              {canEdit ? (
                <EditableNumRow label="Start-up" value={h.start_up_charge} onSave={(v) => onUpdateHeading({ start_up_charge: v }, "Start-up updated")} currency={currency} testid="quote-startup" />
              ) : (Number(h.start_up_charge) > 0 && <SumRow label="Start-up" value={formatMoney(h.start_up_charge, currency)} />)}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200 mt-2">
                <span className="text-xs font-black uppercase tracking-wider text-slate-700">Total</span>
                <span data-testid="quote-total" className="text-xl font-black font-mono text-slate-900">{formatMoney(h.total, currency)}</span>
              </div>
            </div>

            {!canEdit && !isInvoice && (
              <p className="text-[10px] text-slate-400 italic mt-4">{t("quote.sign_in_to_edit", "Sign in as a staff member to edit this quote.")}</p>
            )}
          </div>

          {/* Related quotes for same customer */}
          <RelatedQuotesPanel
            quotes={related}
            currentId={id}
            currency={currency}
            onNavigate={(otherId) => navigate(`/quote/${encodeURIComponent(otherId)}${magicToken ? `?t=${encodeURIComponent(magicToken)}` : ""}`)}
          />
          {/* iter150AI.2 — Audit trail of submit/approve/reject events. */}
          <ApprovalHistoryTimeline headingId={id} dealerState={dealerState} />
        </aside>
      </main>
      {/* iter150AI.2 — In-app guided tutorial. */}
      <DealerQuoteWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}

function QuoteLineRow({ line, currency, canEdit, saving, onUpdateQty, onUpdatePrice, onDelete }) {
  const t = useT();
  const qty = Number(line.Quantity) || 0;
  const price = Number(line.price) || 0;
  const discount = Number(line.discount) || 0;
  const total = qty * price * (1 - discount);
  const [qtyLocal, setQtyLocal] = useState(qty);
  const [priceLocal, setPriceLocal] = useState(price);

  // Keep local state in sync when server state changes
  useEffect(() => { setQtyLocal(qty); }, [qty]);
  useEffect(() => { setPriceLocal(price); }, [price]);

  const commitQty = () => {
    if (qtyLocal !== qty) onUpdateQty(line.QuoteInvoiceDetailID, qtyLocal);
  };
  const commitPrice = () => {
    if (Number(priceLocal) !== price) onUpdatePrice(line.QuoteInvoiceDetailID, priceLocal);
  };

  return (
    <tr data-testid={`quote-line-${line.QuoteInvoiceDetailID}`} className="border-b border-slate-100 last:border-b-0">
      <td className="px-3 py-2 align-top">
        <p className="font-semibold text-slate-900">{line.product_name || line.product_code}</p>
        {line.product_name && !String(line.product_code || "").startsWith("LABOR-") && (
          <p className="text-[10px] font-mono text-slate-400">{line.product_code}</p>
        )}
        {line.explanation && <p className="text-[11px] text-slate-500 italic mt-0.5">{line.explanation}</p>}
      </td>
      <td className="px-3 py-2 text-right">
        {canEdit ? (
          <div className="inline-flex items-center border border-slate-200 rounded-sm">
            <button
              data-testid={`quote-qty-dec-${line.QuoteInvoiceDetailID}`}
              onClick={() => { const n = Math.max(1, qtyLocal - 1); setQtyLocal(n); onUpdateQty(line.QuoteInvoiceDetailID, n); }}
              disabled={qtyLocal <= 1 || saving}
              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30"
            ><Minus size={10} /></button>
            <input
              data-testid={`quote-qty-input-${line.QuoteInvoiceDetailID}`}
              type="number" min="1" value={qtyLocal}
              onChange={(e) => setQtyLocal(e.target.value)}
              onBlur={commitQty}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              disabled={saving}
              className="w-12 h-7 text-xs text-center border-x border-slate-200 focus:outline-none focus:bg-blue-50"
            />
            <button
              data-testid={`quote-qty-inc-${line.QuoteInvoiceDetailID}`}
              onClick={() => { const n = qtyLocal + 1; setQtyLocal(n); onUpdateQty(line.QuoteInvoiceDetailID, n); }}
              disabled={saving}
              className="w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-30"
            ><Plus size={10} /></button>
          </div>
        ) : (
          <span className="font-mono text-slate-700">{qty}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {canEdit ? (
          <input
            data-testid={`quote-price-input-${line.QuoteInvoiceDetailID}`}
            type="number" min="0" step="0.01" value={priceLocal}
            onChange={(e) => setPriceLocal(e.target.value)}
            onBlur={commitPrice}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            disabled={saving}
            className="w-24 h-7 text-xs text-right font-mono border border-slate-200 rounded-sm px-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span className="font-mono text-slate-700">{formatMoney(price, currency)}</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
        {formatMoney(total, currency)}
        {discount > 0 && <p className="text-[9px] text-slate-400">-{(discount * 100).toFixed(0)}% disc</p>}
      </td>
      {canEdit && (
        <td className="px-3 py-2 text-center">
          <button
            data-testid={`quote-delete-${line.QuoteInvoiceDetailID}`}
            disabled={saving}
            onClick={() => onDelete(line.QuoteInvoiceDetailID, line.product_name || line.product_code)}
            className="text-slate-400 hover:text-red-600 transition-colors disabled:opacity-30"
            title={t("quote.delete_line", "Delete line")}
          >
            {saving ? <Spinner size={12} className="animate-spin" /> : <Trash size={12} />}
          </button>
        </td>
      )}
    </tr>
  );
}

function HField({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className={`text-sm text-slate-800 ${mono ? "font-mono" : ""} break-words`}>{value || "—"}</p>
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-slate-700">{value}</span>
    </div>
  );
}

function EditableNumRow({ label, value, onSave, currency, testid }) {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => { setV(value ?? 0); }, [value]);
  const commit = () => {
    const n = Number(v) || 0;
    if (n !== Number(value || 0)) onSave(n);
  };
  return (
    <div className="flex items-center justify-between text-xs gap-2">
      <span className="text-slate-500">{label}</span>
      <div className="flex items-center gap-1">
        <input
          data-testid={testid}
          type="number" min="0" step="0.01" value={v}
          onChange={(e) => setV(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="w-20 h-7 text-xs text-right font-mono border border-slate-200 rounded-sm px-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <span className="text-[10px] text-slate-400 font-mono">{currency === "CAD" ? "C$" : "$"}</span>
      </div>
    </div>
  );
}

// ─── Change history ─────────────────────────────────────────────────
const ACTION_BADGE = {
  CREATE: "bg-emerald-50 border-emerald-200 text-emerald-700",
  UPDATE: "bg-blue-50 border-blue-200 text-blue-700",
  DELETE: "bg-red-50 border-red-200 text-red-700",
  EMAIL:  "bg-amber-50 border-amber-200 text-amber-700",
};
const ENTITY_ICON = {
  cart_checkout: ShoppingCart,
  quote: PencilSimple,
  quote_line: Receipt,
  email: EnvelopeSimple,
};
// Delivery-status pill for EMAIL rows. Keys match ActionLog details.status
// AND the live email_sends.status (the live doc wins when a send_id is
// present in the history event, so opens/bounces surface in real-time).
const DELIVERY_PILL = {
  delivered:   { label: "Delivered",   cls: "bg-emerald-100 border-emerald-300 text-emerald-800", icon: "✓" },
  opened:      { label: "Opened",      cls: "bg-emerald-200 border-emerald-400 text-emerald-900", icon: "👁" },
  bounced:     { label: "Bounced",     cls: "bg-red-100 border-red-300 text-red-900",             icon: "✗" },
  spam:        { label: "Marked spam", cls: "bg-red-100 border-red-300 text-red-900",             icon: "🚫" },
  auth_failed: { label: "Auth failed", cls: "bg-red-100 border-red-300 text-red-800",             icon: "⚠" },
  error:       { label: "SMTP error",  cls: "bg-red-100 border-red-300 text-red-800",             icon: "⚠" },
  mocked:      { label: "Mocked",      cls: "bg-slate-100 border-slate-300 text-slate-600",       icon: "📬" },
};

function QuoteHistorySection({ events, loading, expanded, onToggle, onRefresh, currency, emailSends = [] }) {
  const t = useT();
  const count = events?.length || 0;
  const last = events?.[0];
  // Build send_id → live-send lookup so HistoryRow can upgrade pills live.
  const sendMap = {};
  for (const s of emailSends) { if (s?.send_id) sendMap[s.send_id] = s; }
  return (
    <div data-testid="quote-history-section" className="bg-white border border-slate-200 rounded-sm">
      <button
        data-testid="quote-history-toggle"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-slate-500" />
          <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">{t("quote.change_history", "Change history")}</h2>
          <span data-testid="quote-history-count" className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">
            {count}
          </span>
          {last && (
            <span className="text-[11px] text-slate-400 truncate hidden md:inline max-w-[260px]">
              last: {formatRel(last.ts_utc)} · {last.action_type}
            </span>
          )}
        </div>
        {expanded ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200">
          {loading ? (
            <div className="p-6 text-center"><Spinner size={16} className="animate-spin text-slate-400 mx-auto" /></div>
          ) : count === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">{t("quote.no_events_logged", "No events logged yet.")}</div>
          ) : (
            <>
              <div className="flex justify-end px-5 py-2 border-b border-slate-100">
                <button
                  data-testid="quote-history-refresh"
                  onClick={onRefresh}
                  className="text-[11px] text-slate-500 hover:text-blue-700 underline"
                >{t("action.refresh", "Refresh")}</button>
              </div>
              <ol className="divide-y divide-slate-100 max-h-[420px] overflow-auto">
                {events.map((ev) => (
                  <HistoryRow key={ev.id} ev={ev} currency={currency} liveSend={sendMap[ev.details?.send_id]} />
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ ev, currency, liveSend }) {
  const t = useT();
  const badge = ACTION_BADGE[ev.action_type] || "bg-slate-100 border-slate-200 text-slate-700";
  const Icon = ENTITY_ICON[ev.entity] || Clock;
  const det = ev.details && typeof ev.details === "object" ? ev.details : {};
  // Live status > logged status (so opens/bounces override "delivered"/"auth_failed")
  const liveStatus = liveSend?.status;
  const effectiveStatus = (liveStatus && liveStatus !== "pending") ? liveStatus : (det.status || "mocked");
  const deliveryPill = ev.entity === "email" ? (DELIVERY_PILL[effectiveStatus] || DELIVERY_PILL.mocked) : null;
  // Open count (only non-proxy opens to avoid Gmail image-proxy noise)
  const realOpens = (liveSend?.opens || []).filter(o => !o?.is_proxy);
  const openCount = realOpens.length;
  const lastOpen = realOpens[realOpens.length - 1];
  // Tooltip text — order: bounce reason > last-opened > raw SMTP message > label
  let pillTip = deliveryPill?.label ?? "";
  if (effectiveStatus === "bounced" && liveSend?.bounced?.reason) {
    pillTip = `Bounce: ${liveSend.bounced.reason}`;
  } else if (effectiveStatus === "opened") {
    pillTip = lastOpen?.ts ? `Last opened ${formatRel(lastOpen.ts)}` : "Opened by recipient";
  } else if (det.smtp_message) {
    pillTip = det.smtp_message;
  }

  // Build a human-readable summary depending on what kind of event this is.
  let summary = ev.entity_label || ev.entity || "—";
  let diffBlock = null;

  if (ev.entity === "cart_checkout") {
    summary = `Cart submitted — ${det.line_count || 0} line(s), total ${formatMoney(det.total, det.currency || currency)}${det.is_new_lead ? " (new customer)" : ""}`;
  } else if (ev.entity === "quote" && ev.action_type === "UPDATE") {
    summary = "Quote heading updated";
    if (det.before && det.after) {
      diffBlock = <DiffTable before={det.before} after={det.after} />;
    }
  } else if (ev.entity === "quote_line") {
    if (ev.action_type === "CREATE") {
      const ins = det.inserted || {};
      summary = `Added line: ${ins.product_code || "—"} × ${ins.quantity ?? "?"} @ ${formatMoney(ins.price, currency)}`;
    } else if (ev.action_type === "DELETE") {
      const r = det.removed || {};
      // Deleted row has short-form keys (pc/q/p)
      const code = r.product_code || r.pc || "—";
      const q = r.quantity ?? r.q;
      const p = r.price ?? r.p;
      summary = `Removed line: ${code} × ${q ?? "?"} @ ${formatMoney(p, currency)}`;
    } else if (ev.action_type === "UPDATE") {
      summary = "Line updated";
      if (det.before && det.after) {
        diffBlock = <DiffTable before={det.before} after={det.after} />;
      }
    }
  } else if (ev.entity === "email") {
    const trig = det.trigger || "—";
    const to = det.to || "—";
    if (trig === "quote_update_notification") {
      summary = `Update notice emailed to ${to}`;
    } else if (trig.startsWith("cart_checkout")) {
      summary = `Confirmation emailed to ${to}`;
    } else {
      summary = `Email: ${trig} → ${to}`;
    }
  } else if (ev.entity === "share_link") {
    summary = "Share link generated";
  }

  return (
    <li data-testid={`history-event-${ev.id}`} className="px-5 py-3 hover:bg-slate-50/50">
      <div className="flex items-start gap-3">
        <Icon size={14} className="mt-0.5 text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${badge}`}>
              {ev.action_type}
            </span>
            <span className="text-[10px] font-mono text-slate-400">{ev.entity}</span>
            {deliveryPill && (
              <span
                data-testid={`history-delivery-${effectiveStatus}`}
                title={pillTip}
                className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 ${deliveryPill.cls}`}
              >
                <span aria-hidden="true">{deliveryPill.icon}</span>
                {deliveryPill.label}
                {openCount > 1 && effectiveStatus === "opened" && (
                  <span className="ml-0.5 bg-white/60 border border-emerald-300 rounded-sm px-1 text-emerald-900">
                    {openCount}×
                  </span>
                )}
              </span>
            )}
            <span className="text-[10px] text-slate-400 ml-auto">
              {formatRel(ev.ts_utc)} · <span className="font-mono">{ev.actor_email}</span>
            </span>
          </div>
          <p className="text-sm text-slate-800">{summary}</p>
          {det.new_total != null && (
            <p className="text-[11px] text-slate-500">{t("quote.new_total", "New total:")} <span className="font-mono">{formatMoney(det.new_total, currency)}</span></p>
          )}
          {diffBlock}
        </div>
      </div>
    </li>
  );
}

function DiffTable({ before, after }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) return null;
  return (
    <div className="mt-2 border border-slate-200 rounded-sm overflow-hidden inline-block">
      <table className="text-[11px]">
        <thead>
          <tr className="bg-slate-50 text-[9px] font-bold uppercase tracking-wider text-slate-500">
            <th className="px-2 py-1 text-left">{t("table.field", "Field")}</th>
            <th className="px-2 py-1 text-left">{t("table.before", "Before")}</th>
            <th className="px-2 py-1 text-left">{t("table.after", "After")}</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k} className="border-t border-slate-100">
              <td className="px-2 py-1 font-mono text-slate-600">{k}</td>
              <td className="px-2 py-1 font-mono text-red-600 line-through">{String(before?.[k] ?? "—")}</td>
              <td className="px-2 py-1 font-mono text-emerald-700">{String(after?.[k] ?? "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatRel(iso) {
  if (!iso) return "—";
  try {
    // Handle both "…Z", "…+00:00", and bare "…" (assume UTC for the last).
    let raw = String(iso);
    const hasTz = raw.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(raw);
    const d = new Date(hasTz ? raw : raw + "Z");
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (isNaN(sec)) return "—";
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

// ─── Related quotes (same customer) ─────────────────────────────────
function RelatedQuotesPanel({ quotes, currentId, currency, onNavigate }) {
  const t = useT();
  if (!quotes || quotes.length === 0) {
    return (
      <div data-testid="quote-related-panel" className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center gap-2 mb-1">
          <Buildings size={14} weight="bold" className="text-slate-500" />
          <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">{t("quote.related_quotes", "Related quotes")}</h2>
        </div>
        <p className="text-[11px] text-slate-400 italic">{t("quote.no_related_quotes", "No other quotes for this customer yet.")}</p>
      </div>
    );
  }
  return (
    <div data-testid="quote-related-panel" className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Buildings size={14} weight="bold" className="text-slate-500" />
        <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">{t("quote.related_quotes", "Related quotes")}</h2>
        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{quotes.length}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {quotes.map((q) => {
          const hid = q.QuoteInvoiceHeadingID;
          const short = hid.startsWith("CART-") ? hid.slice(5, 13) : hid.slice(0, 8);
          const isInv = !!q.invoice_number;
          return (
            <li key={hid}>
              <button
                data-testid={`related-quote-${hid}`}
                onClick={() => onNavigate(hid)}
                disabled={hid === currentId}
                className={`w-full text-left py-2 px-1 hover:bg-slate-50 flex items-start gap-2 rounded-sm transition-colors ${hid === currentId ? "opacity-40 cursor-default" : ""}`}
              >
                <Receipt size={12} className={`mt-0.5 ${isInv ? "text-amber-600" : "text-blue-600"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-bold font-mono text-slate-900 truncate">#{short}</p>
                  <p className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                    <span>{q.quote_date || q.invoice_date || "—"} · {q.Status || (isInv ? "Invoice" : "Quote")}</span>
                    {q.payment_status && q.payment_status !== "none" && (
                      <span className={`text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${
                        q.payment_status === "paid"    ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        q.payment_status === "partial" ? "bg-amber-50 text-amber-800 border-amber-300" :
                                                         "bg-rose-50 text-rose-700 border-rose-200"
                      }`} data-testid={`related-pay-${hid}-${q.payment_status}`}>
                        {q.payment_status}
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-[11px] font-mono font-bold text-slate-700 whitespace-nowrap">
                  {formatMoney(q.total, currency)}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Version diff ──────────────────────────────────────────────────
function QuoteVersionDiffSection({ versions, expanded, onToggle, currency }) {
  const t = useT();
  const hasData = !!versions;
  const hasSnapshot = !!versions?.has_snapshot;
  const count = versions?.diff_count || 0;
  const diffs = versions?.diffs || [];

  return (
    <div data-testid="quote-versions-section" className="bg-white border border-slate-200 rounded-sm">
      <button
        data-testid="quote-versions-toggle"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <GitBranch size={14} weight="bold" className="text-slate-500" />
          <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">{t("quote.what_changed_v1", "What changed since v1")}</h2>
          <span data-testid="quote-versions-count" className={`text-[10px] font-bold border px-1.5 py-0.5 rounded-sm ${count > 0 ? "text-blue-700 bg-blue-50 border-blue-200" : "text-slate-500 bg-slate-100 border-slate-200"}`}>
            {hasSnapshot ? `${count} change${count === 1 ? "" : "s"}` : "no snapshot"}
          </span>
        </div>
        {expanded ? <CaretUp size={14} className="text-slate-400" /> : <CaretDown size={14} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-slate-200 p-5 space-y-3">
          {!hasData ? (
            <p className="text-xs text-slate-400"><Spinner size={12} className="inline animate-spin mr-1" /> Loading…</p>
          ) : !hasSnapshot ? (
            <p data-testid="quote-versions-no-snapshot" className="text-xs text-slate-500 italic">
              {t("quote.no_snapshot_hint", "This quote was created before we started snapshotting initial versions. A fresh diff baseline will be captured on the next cart-checkout.")}
            </p>
          ) : count === 0 ? (
            <p data-testid="quote-versions-unchanged" className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle size={12} weight="fill" /> Identical to the initial version — nothing has changed.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="quote-versions-list">
              {diffs.map((d, i) => (
                <VersionDiffRow key={i} diff={d} currency={currency} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function VersionDiffRow({ diff, currency }) {
  const kind = diff.kind;
  const label = diff.product_name || diff.product_code || (kind === "heading" ? "Quote totals" : "—");
  const KIND_STYLE = {
    added: { bar: "bg-emerald-500", tag: "ADDED", pill: "bg-emerald-50 border-emerald-200 text-emerald-700", Icon: Plus },
    removed: { bar: "bg-red-500", tag: "REMOVED", pill: "bg-red-50 border-red-200 text-red-700", Icon: Minus },
    changed: { bar: "bg-blue-500", tag: "CHANGED", pill: "bg-blue-50 border-blue-200 text-blue-700", Icon: PencilSimple },
    heading: { bar: "bg-slate-500", tag: "HEADING", pill: "bg-slate-50 border-slate-200 text-slate-600", Icon: ListChecks },
  };
  const sty = KIND_STYLE[kind] || KIND_STYLE.changed;
  const Icon = sty.Icon;
  const changes = diff.changes || {};
  const beforeItem = diff.before || {};
  const afterItem = diff.after || {};

  return (
    <li data-testid={`diff-row-${kind}-${(diff.product_code || "heading").toLowerCase()}`} className="flex items-start gap-3 border border-slate-100 rounded-sm p-3 bg-slate-50/40">
      <div className={`w-1 self-stretch rounded ${sty.bar}`}></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon size={11} className="text-slate-500" />
          <p className="text-sm font-semibold text-slate-900 truncate">{label}</p>
          <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${sty.pill}`}>{sty.tag}</span>
        </div>
        {kind === "added" && (
          <p className="text-[11px] text-slate-600">
            <span className="font-mono">{afterItem.product_code}</span> · qty {Number(afterItem.quantity || 0)} @ {formatMoney(afterItem.price, currency)}
          </p>
        )}
        {kind === "removed" && (
          <p className="text-[11px] text-slate-600">
            <span className="font-mono">{beforeItem.product_code}</span> · qty {Number(beforeItem.quantity || 0)} @ {formatMoney(beforeItem.price, currency)}
          </p>
        )}
        {(kind === "changed" || kind === "heading") && Object.keys(changes).length > 0 && (
          <div className="mt-1 overflow-x-auto">
            <table className="text-[11px]">
              <thead>
                <tr className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="pr-3 text-left">{t("table.field", "Field")}</th>
                  <th className="pr-3 text-left">v1</th>
                  <th className="pr-3 text-left">{t("table.now", "Now")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(changes).map(([k, v]) => {
                  const isMoney = ["price", "Shipping", "sales_tax", "start_up_charge", "total"].includes(k);
                  const fmt = (x) => (x === null || x === undefined) ? "—"
                    : isMoney ? formatMoney(Number(x || 0), currency)
                    : String(x);
                  return (
                    <tr key={k} className="border-t border-slate-100">
                      <td className="py-1 pr-3 font-mono text-slate-500">{k}</td>
                      <td className="py-1 pr-3 font-mono text-red-600 line-through">{fmt(v.before)}</td>
                      <td className="py-1 pr-3 font-mono text-emerald-700">{fmt(v.after)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </li>
  );
}



// iter150AI — Dealer workflow banner. Renders status-specific UI:
//  • Draft / Rejected → "Submit for approval" button (dealer)
//  • Pending Approval → yellow "Awaiting approval" banner
//  • Approved        → green locked banner
function DealerWorkflowBar({ dealerState, headingId, onChanged, onError, onBanner, onShowWizard }) {
  const [busy, setBusy] = useState(false);
  if (!dealerState) return null;
  const status = (dealerState.status || "").trim();

  const tourBtn = onShowWizard ? (
    <button type="button" onClick={onShowWizard}
            data-testid="dealer-quote-tour-trigger"
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-white/60 hover:bg-white text-slate-800 border border-slate-300 inline-flex items-center gap-1">
      <BookOpen size={10} weight="bold" />
      Quick tour
    </button>
  ) : null;

  async function submit() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await submitQuoteForApproval(headingId);
      const n = (r.recipients || []).length;
      onBanner && onBanner(`Quote submitted for approval — emailed to ${n} recipient${n === 1 ? "" : "s"}.`);
      onChanged && (await onChanged());
    } catch (e) {
      onError && onError(e?.response?.data?.detail || e?.message || "Submit failed");
    } finally { setBusy(false); }
  }

  if (status === "Pending Approval") {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 mt-3">
        <div data-testid="dealer-status-pending"
             className="bg-amber-50 border border-amber-300 rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap">
          <Clock size={14} weight="bold" className="text-amber-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-amber-900">Awaiting approval</span>
          <span className="text-[11px] text-amber-900">
            — product lines and discounts are locked until an approver (admin, super-admin, or the customer's account rep) clicks Approve in the email. Shipping, tax, start-up charge and notes may still be edited.
          </span>
          <span className="ml-auto">{tourBtn}</span>
        </div>
      </div>
    );
  }
  if (status === "Approved") {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 mt-3">
        <div data-testid="dealer-status-approved"
             className="bg-emerald-50 border border-emerald-300 rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap">
          <CheckCircle size={14} weight="fill" className="text-emerald-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-900">Approved & locked</span>
          <span className="text-[11px] text-emerald-900">
            Prepared by <b>{dealerState.prepared_by || "—"}</b>. Only an admin can re-open.
          </span>
          <span className="ml-auto">{tourBtn}</span>
        </div>
      </div>
    );
  }
  // Draft / Rejected / legacy "Quote" — show submit button if the user can.
  if (!dealerState.can_submit) return null;
  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 mt-3">
      <div data-testid="dealer-status-draft"
           className="bg-blue-50 border border-blue-300 rounded-sm px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <PencilSimple size={14} weight="bold" className="text-blue-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-blue-900">
            {status === "Rejected" ? "Rejected — edit and resubmit" : "Draft"}
          </span>
          <span className="text-[11px] text-blue-900">
            Click <b>Submit for approval</b> when the quote is ready. An email with Approve/Reject buttons will be sent to the customer's rep + the configured approver list.
          </span>
        </div>
        <button type="button" onClick={submit} disabled={busy}
                data-testid="dealer-submit-for-approval"
                className="text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy ? "Submitting…" : "Submit for approval"}
        </button>
        {tourBtn}
      </div>
    </div>
  );
}

